import { DesignDialog as Dialog } from "../design/DesignDialog.js";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  mapToPlateauStreams,
  plateauStreamsToMap,
  type PlateauStreamLink,
} from "../../../util/src/live-stream-url";
import dico from "../../../util/src/language/dico";

export type LiveStreamLinksSavePayload = {
  streamUrl: string | null;
  plateauStreams: PlateauStreamLink[];
};

export type LiveStreamLinksModalProps = {
  open: boolean;
  onClose: () => void;
  initialStreamUrl?: string | null;
  initialPlateauStreams?: PlateauStreamLink[] | null;
  plateauNumbers: number[];
  saving?: boolean;
  onSave: (payload: LiveStreamLinksSavePayload) => void | Promise<void>;
};

/**
 * Dialog to edit the global live stream URL and optional per-plateau overrides.
 */
const LiveStreamLinksModal: React.FC<LiveStreamLinksModalProps> = ({
  open,
  onClose,
  initialStreamUrl,
  initialPlateauStreams,
  plateauNumbers,
  saving = false,
  onSave,
}) => {
  const labels = dico().stream.plateauLinks;
  const [globalUrl, setGlobalUrl] = useState("");
  const [byPlateau, setByPlateau] = useState<Record<string, string>>({});
  // Hydrate only when the dialog opens - ignore initial* updates while open
  // (parent refresh on save would otherwise reset fields and flash before close).
  useEffect(() => {
    if (!open) {
      return;
    }
    setGlobalUrl(initialStreamUrl?.trim() ?? "");
    setByPlateau(plateauStreamsToMap(initialPlateauStreams));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open-only hydrate
  }, [open]);
  const handlePlateauChange = (plateauNumber: number, value: string) => {
    setByPlateau((prev) => ({ ...prev, [String(plateauNumber)]: value }));
  };
  const handleSave = async () => {
    const cleanedMap: Record<string, string> = {};
    for (const n of plateauNumbers) {
      const value = byPlateau[String(n)]?.trim() ?? "";
      if (value) {
        cleanedMap[String(n)] = value;
      }
    }
    await onSave({
      streamUrl: globalUrl.trim() ? globalUrl.trim() : null,
      plateauStreams: mapToPlateauStreams(cleanedMap),
    });
  };
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{labels.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={labels.globalLabel}
            value={globalUrl}
            onChange={(e) => setGlobalUrl(e.target.value)}
            placeholder={labels.urlPlaceholder}
            helperText={labels.globalHelper}
            fullWidth
            disabled={saving}
          />
          {plateauNumbers.length > 0 && (
            <Typography variant="subtitle2" color="text.secondary">
              {labels.title}
            </Typography>
          )}
          {plateauNumbers.map((n) => (
            <TextField
              key={n}
              label={labels.plateauLabel(n)}
              value={byPlateau[String(n)] ?? ""}
              onChange={(e) => handlePlateauChange(n, e.target.value)}
              placeholder={labels.urlPlaceholder}
              fullWidth
              disabled={saving}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined">
          {labels.cancel}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {labels.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LiveStreamLinksModal;
